import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

// ─── Config ──────────────────────────────────────────────────────────────────

export interface ConfigSngpcData {
  ativo: boolean;
  email?: string | null;
  cpfTransmissor?: string | null;
  usarApiNova: boolean;
  temSenha: boolean;
}

export interface SalvarConfigSngpcRequest {
  ativo: boolean;
  email?: string | null;
  senha?: string | null; // em branco mantém a atual
  cpfTransmissor?: string | null;
  usarApiNova: boolean;
}

export const useSngpcConfig = () => {
  const qc = useQueryClient();
  const key = ["sngpc-config"];

  const query = useQuery({
    queryKey: key,
    queryFn: async () => (await apiClient.get<ConfigSngpcData>("/api/sngpc/config")).data,
  });

  const salvar = useMutation({
    mutationFn: async (body: SalvarConfigSngpcRequest) =>
      (await apiClient.put<ConfigSngpcData>("/api/sngpc/config", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Configuração SNGPC salva!"); },
    onError: (e: any) => toast.error(e?.response?.data?.mensagem ?? `Erro: ${e.message}`),
  });

  return { config: query.data, isLoading: query.isLoading, salvarConfig: salvar.mutateAsync, salvando: salvar.isPending };
};

// ─── Transmissões ────────────────────────────────────────────────────────────

export interface TransmissaoData {
  id: number;
  tipo: string;
  dtInicioPeriodo?: string | null;
  dtFimPeriodo?: string | null;
  dtReferencia?: string | null;
  dtGeracao: string;
  dtEnvio?: string | null;
  hashMd5?: string | null;
  canal: string;
  status: string;
  retornoAnvisa?: string | null;
  mensagemErro?: string | null;
}

export interface TransmissaoResult {
  sucesso: boolean;
  id: number;
  hash?: string | null;
  mensagem: string;
}

export const useSngpcTransmissoes = () => {
  const qc = useQueryClient();
  const key = ["sngpc-transmissoes"];
  const base = "/api/sngpc/transmissoes";

  const query = useQuery({
    queryKey: key,
    queryFn: async () => (await apiClient.get<TransmissaoData[]>(base)).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const toastResult = (r: TransmissaoResult) =>
    r.sucesso ? toast.success(r.mensagem || "Operação concluída.") : toast.error(r.mensagem || "Falha na operação.");

  const enviarMovimentacao = useMutation({
    mutationFn: async (body: { inicio: string; fim: string; validarXsd: boolean }) =>
      (await apiClient.post<TransmissaoResult>(`${base}/movimentacao`, body)).data,
    onSuccess: (r) => { invalidate(); toastResult(r); },
    onError: (e: any) => toast.error(e?.response?.data?.mensagem ?? `Erro: ${e.message}`),
  });

  const enviarInventario = useMutation({
    mutationFn: async (body: { dataReferencia: string; validarXsd: boolean }) =>
      (await apiClient.post<TransmissaoResult>(`${base}/inventario`, body)).data,
    onSuccess: (r) => { invalidate(); toastResult(r); },
    onError: (e: any) => toast.error(e?.response?.data?.mensagem ?? `Erro: ${e.message}`),
  });

  const reenviar = useMutation({
    mutationFn: async (id: number) =>
      (await apiClient.post<TransmissaoResult>(`${base}/${id}/reenviar`, {})).data,
    onSuccess: (r) => { invalidate(); toastResult(r); },
    onError: (e: any) => toast.error(e?.response?.data?.mensagem ?? `Erro: ${e.message}`),
  });

  const consultarStatus = useMutation({
    mutationFn: async (id: number) =>
      (await apiClient.post<TransmissaoResult>(`${base}/${id}/consultar-status`, {})).data,
    onSuccess: (r) => { invalidate(); toastResult(r); },
    onError: (e: any) => toast.error(e?.response?.data?.mensagem ?? `Erro: ${e.message}`),
  });

  return {
    transmissoes: query.data ?? [],
    isLoading: query.isLoading,
    enviarMovimentacao: enviarMovimentacao.mutateAsync,
    enviarInventario: enviarInventario.mutateAsync,
    reenviar: reenviar.mutate,
    consultarStatus: consultarStatus.mutate,
    enviando: enviarMovimentacao.isPending || enviarInventario.isPending,
  };
};

/** Busca o XML completo de uma transmissão (texto). */
export async function obterXmlTransmissao(id: number): Promise<string> {
  const resp = await apiClient.get<string>(`/api/sngpc/transmissoes/${id}/xml`, {
    responseType: "text",
    transformResponse: [(d) => d], // evita parse automático
  });
  return resp.data;
}
